import { useState, useEffect, useRef, useCallback } from 'react'
import { searchApi, type MessageHit } from '../../api/search'
import { usersApi } from '../../api/users'
import { chatsApi } from '../../api/chats'
import { useChatStore } from '../../stores/chatStore'
import { useUiStore } from '../../stores/uiStore'
import { Avatar } from './Avatar'
import { Spinner } from './Spinner'
import type { Chat, User } from '../../types'
import { chatTitle, userName } from '../../lib/displayName'

interface Props { onClose: () => void }

function useDebounce<T>(value: T, delay: number): T {
  const [d, setD] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setD(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return d
}

type Section = 'chats' | 'users' | 'messages'

interface Result {
  section: Section
  id: string
  // chat result
  chat?: Chat
  // user result
  user?: User
  // message result
  message?: MessageHit
  chatOfMessage?: Chat
}

function highlight(text: string, query: string) {
  if (!query || query.length < 2) return <>{text}</>
  const idx = text.toLowerCase().indexOf(query.toLowerCase())
  if (idx === -1) return <>{text}</>
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-accent/30 text-txt-primary rounded px-0.5">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  )
}

export function GlobalSearch({ onClose }: Props) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Result[]>([])
  const [loading, setLoading] = useState(false)
  const [activeIdx, setActiveIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const { addToast } = useUiStore()
  const dq = useDebounce(query, 280)

  useEffect(() => { inputRef.current?.focus() }, [])

  useEffect(() => {
    if (dq.length < 2) { setResults([]); return }

    // Read chats snapshot at search time via getState() — NOT a reactive dep.
    // Having `chats` as a dep caused an infinite loop: ChatList's new
    // setChats([]) on mount → GlobalSearch re-renders → effect re-runs →
    // setResults([]) → re-render → repeat until React error #185.
    const chats = useChatStore.getState().chats

    const lower = dq.toLowerCase()
    setLoading(true)

    // 1. Local chat search (instant)
    const chatHits: Result[] = chats
      .filter((c) => {
        const title = chatTitle(c)
        const username = c.peer?.username ?? ''
        return title.toLowerCase().includes(lower) || username.toLowerCase().includes(lower)
      })
      .slice(0, 5)
      .map((c) => ({ section: 'chats' as Section, id: `chat-${c.id}`, chat: c }))

    setResults(chatHits)
    setActiveIdx(0)

    // 2. User search (server)
    const doSearch = async () => {
      try {
        const [usersRes] = await Promise.all([
          usersApi.search(dq).catch(() => ({ users: [] as User[] })),
        ])

        const userHits: Result[] = (usersRes.users ?? [])
          .slice(0, 5)
          .map((u) => ({ section: 'users' as Section, id: `user-${u.id}`, user: u }))

        // 3. Message search: search in the 5 most-recently-active chats
        const recentChats = [...chats]
          .sort((a, b) => {
            const ta = a.last_message?.created_at ?? a.created_at ?? ''
            const tb = b.last_message?.created_at ?? b.created_at ?? ''
            return tb.localeCompare(ta)
          })
          .slice(0, 5)

        const msgResults = await Promise.all(
          recentChats.map((c) =>
            searchApi.inChat(c.id, dq, 3).then((msgs) =>
              msgs.map((m): Result => ({
                section: 'messages',
                id: `msg-${m.id}`,
                message: m,
                chatOfMessage: c,
              }))
            ).catch(() => [] as Result[])
          )
        )
        const msgHits = msgResults.flat().slice(0, 10)

        setResults([...chatHits, ...userHits, ...msgHits])
      } finally {
        setLoading(false)
      }
    }
    doSearch()
  }, [dq])

  const openResult = useCallback(async (r: Result) => {
    onClose()
    if (r.chat) {
      useChatStore.getState().setActiveChat(r.chat.id)
    } else if (r.user) {
      try {
        const chat = await chatsApi.getOrCreate(r.user.id)
        useChatStore.getState().upsertChat(chat)
        useChatStore.getState().setActiveChat(chat.id)
      } catch {
        addToast('error', 'Не удалось открыть чат')
      }
    } else if (r.message) {
      useChatStore.getState().setActiveChat(r.message.chat_id)
      useUiStore.getState().setHighlightedMessage(r.message.id)
    }
  }, [onClose, addToast])

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { onClose(); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, results.length - 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)) }
    if (e.key === 'Enter' && results[activeIdx]) openResult(results[activeIdx])
  }

  // Group results by section for rendering
  const allSections: { key: Section; label: string; items: Result[] }[] = [
    { key: 'chats' as Section, label: 'Чаты', items: results.filter(r => r.section === 'chats') },
    { key: 'users' as Section, label: 'Пользователи', items: results.filter(r => r.section === 'users') },
    { key: 'messages' as Section, label: 'Сообщения', items: results.filter(r => r.section === 'messages') },
  ]
  const sections = allSections.filter(s => s.items.length > 0)

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh] bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-bg-primary rounded-2xl shadow-2xl overflow-hidden mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-divider">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
               className="text-txt-meta shrink-0" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Поиск чатов, пользователей, сообщений..."
            className="flex-1 bg-transparent text-txt-primary text-[15px] outline-none placeholder-txt-meta"
          />
          {loading && <Spinner size={16} />}
          <kbd className="text-txt-meta text-xs border border-divider rounded px-1.5 py-0.5 shrink-0">Esc</kbd>
        </div>

        {/* Results */}
        <div className="max-h-[65vh] overflow-y-auto">
          {query.length < 2 && (
            <div className="flex flex-col items-center py-10 gap-2 text-center text-txt-meta text-sm">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                   strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              Введите минимум 2 символа
            </div>
          )}
          {query.length >= 2 && !loading && results.length === 0 && (
            <div className="flex flex-col items-center py-10 gap-2 text-center text-txt-meta text-sm">
              Ничего не найдено по «{query}»
            </div>
          )}

          {sections.map((section) => {
            let globalOffset = 0
            for (const s of sections) {
              if (s.key === section.key) break
              globalOffset += s.items.length
            }
            return (
              <div key={section.key}>
                <div className="px-4 py-1.5 text-[11px] font-semibold text-txt-meta uppercase tracking-wide border-b border-divider/50 bg-bg-secondary/50">
                  {section.label}
                </div>
                {section.items.map((r, localIdx) => {
                  const idx = globalOffset + localIdx
                  const isActive = idx === activeIdx
                  return (
                    <button
                      key={r.id}
                      onClick={() => openResult(r)}
                      onMouseEnter={() => setActiveIdx(idx)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors
                        ${isActive ? 'bg-bg-hover' : 'hover:bg-bg-hover'}`}
                    >
                      {r.chat && (
                        <>
                          <Avatar
                            name={chatTitle(r.chat)}
                            src={r.chat.avatar_url ?? r.chat.peer?.avatar_url}
                            size={38}
                            online={r.chat.peer?.online}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-txt-primary text-sm font-medium truncate">
                              {highlight(chatTitle(r.chat), query)}
                            </p>
                            {r.chat.peer?.username && (
                              <p className="text-txt-meta text-xs">@{r.chat.peer.username}</p>
                            )}
                          </div>
                        </>
                      )}
                      {r.user && (
                        <>
                          <Avatar
                            name={userName(r.user)}
                            src={r.user.avatar_url}
                            size={38}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-txt-primary text-sm font-medium truncate">
                              {highlight(userName(r.user), query)}
                            </p>
                            {r.user.username && <p className="text-txt-meta text-xs">@{r.user.username}</p>}
                          </div>
                          {r.user.is_bot && (
                            <span className="text-[10px] bg-accent/15 text-accent px-1.5 py-0.5 rounded-full shrink-0">bot</span>
                          )}
                        </>
                      )}
                      {r.message && (
                        <>
                          <div className="w-9 h-9 rounded-full bg-bg-input flex items-center justify-center shrink-0">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                 className="text-txt-meta" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-txt-meta text-xs mb-0.5 truncate">
                              {chatTitle(r.chatOfMessage)}
                              {' · '}
                              {new Date(r.message.created_at).toLocaleDateString('ru', { day: 'numeric', month: 'short' })}
                            </p>
                            <p className="text-txt-primary text-sm line-clamp-2 leading-snug">
                              {highlight(r.message.text, query)}
                            </p>
                          </div>
                        </>
                      )}
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
